import { useAuthContext } from '@/providers';

// ==============================|| PERMISSION GATE - useHasPermission ||============================== //

export type PermissionMode = 'all' | 'any';

type PermissionUser = {
	role?: string;
	permissions?: string[];
};

/**
 * Pure permission check (no hooks) — the single rule reused by the route-level
 * gate and the nomenclature tab filter.
 * - `mode: 'all'` (default) requires every key; `'any'` requires at least one.
 * - The Superadmin role bypasses every check, mirroring the backend
 *   PermissionAuthorizationHandler (IsInRole(Superadmin) || HasClaim(permission)).
 * - An empty / missing permission means "no restriction" → allowed.
 */
export const hasPermission = (
	user: PermissionUser | null | undefined,
	permission: string | string[] | undefined,
	mode: PermissionMode = 'all'
): boolean => {
	if (user?.role === 'superadmin') return true;

	const required = (Array.isArray(permission) ? permission : permission ? [permission] : []).filter(
		Boolean
	);
	if (required.length === 0) return true;

	const granted = new Set(user?.permissions ?? []);
	return mode === 'all'
		? required.every((p) => granted.has(p))
		: required.some((p) => granted.has(p));
};

/**
 * Hook wrapper around {@link hasPermission} for the logged-in user.
 */
export const useHasPermission = (
	permission: string | string[],
	mode: PermissionMode = 'all'
): boolean => {
	const { user } = useAuthContext();
	return hasPermission(user as PermissionUser | null | undefined, permission, mode);
};
